import { todayYmdZurich } from "./schoolAttention";

const STORAGE_KEY = "albis.homeSnapshot";
const EVENING_FROM_HOUR = 17;

const ITEM_KEYS = [
  "id",
  "title",
  "date",
  "end_date",
  "time",
  "all_day",
  "place",
  "child",
  "remindEnabled",
  "action_type",
  "overdue",
  "origin",
  "sourceKind",
  "visibility",
  "isOwn",
  "householdId",
  "match_key",
];

/**
 * @param {Date} [now]
 * @returns {number}
 */
export function zurichHour(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Zurich",
    hour: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const raw = parts.find((part) => part.type === "hour")?.value || "0";
  return parseInt(raw, 10) % 24;
}

/** @param {Date} [now] @returns {'morning'|'evening'} */
export function briefingKindNow(now = new Date()) {
  return zurichHour(now) >= EVENING_FROM_HOUR ? "evening" : "morning";
}

/**
 * @param {object} item
 */
function thinItem(item) {
  /** @type {Record<string, unknown>} */
  const next = {};
  for (const key of ITEM_KEYS) {
    if (item?.[key] !== undefined) next[key] = item[key];
  }
  if (typeof next.remindEnabled === "boolean") next.remind = next.remindEnabled;
  return next;
}

/**
 * @param {object} date
 */
function thinSchoolDate(date) {
  if (!date || typeof date !== "object") return null;
  return {
    key: date.key || "",
    childId: date.childId || null,
    childName: date.childName || "",
    name: date.name || "",
    kind: date.kind || "",
    startsOn: date.startsOn || "",
    endsOn: date.endsOn || "",
    packId: date.packId || "",
    packLabel: date.packLabel || "",
  };
}

/**
 * @param {object} event
 */
function thinHoliday(event) {
  if (!event || typeof event !== "object") return null;
  return {
    kind: event.kind || "",
    name: event.name || "",
    startsOn: event.startsOn || "",
    endsOn: event.endsOn || "",
  };
}

/**
 * @param {string} userId
 * @param {{
 *   items?: object[],
 *   schoolDates?: object[],
 *   nearHolidays?: object[],
 *   hasMail?: boolean|null,
 *   greeting?: { date?: string, kind?: string, text?: string }|null,
 * }} snapshot
 */
export function writeHomeSnapshot(userId, snapshot) {
  if (!userId) return;
  const greeting = snapshot?.greeting;
  const text = String(greeting?.text || "").trim();
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        userId,
        items: (snapshot?.items || []).map(thinItem).filter((item) => item.id && item.date),
        schoolDates: (snapshot?.schoolDates || []).map(thinSchoolDate).filter(Boolean),
        nearHolidays: (snapshot?.nearHolidays || []).map(thinHoliday).filter(Boolean),
        hasMail: snapshot?.hasMail === true ? true : snapshot?.hasMail === false ? false : null,
        greeting: text && greeting?.date && greeting?.kind
          ? { date: greeting.date, kind: greeting.kind, text }
          : null,
      }),
    );
  } catch {
    /* ignore quota and private mode */
  }
}

/**
 * @param {string} userId
 * @param {{ today?: string, kind?: 'morning'|'evening' }} [opts]
 * @returns {null | {
 *   items: object[],
 *   schoolDates: object[],
 *   nearHolidays: object[],
 *   hasMail: boolean|null,
 *   greeting: { date: string, kind: string, text: string }|null,
 * }}
 */
export function readHomeSnapshot(userId, opts = {}) {
  if (!userId) return null;
  const today = opts.today || todayYmdZurich();
  const kind = opts.kind || briefingKindNow();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.userId !== userId) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const items = (Array.isArray(parsed.items) ? parsed.items : []).map((item) => {
      if (item?.date && item.date < today) return { ...item, overdue: true };
      return item;
    });
    const greeting = parsed.greeting;
    const greetingOk = Boolean(
      greeting?.text && greeting.date === today && greeting.kind === kind,
    );
    return {
      items,
      schoolDates: Array.isArray(parsed.schoolDates) ? parsed.schoolDates : [],
      nearHolidays: Array.isArray(parsed.nearHolidays) ? parsed.nearHolidays : [],
      hasMail: parsed.hasMail === true ? true : parsed.hasMail === false ? false : null,
      greeting: greetingOk
        ? { date: greeting.date, kind: greeting.kind, text: greeting.text }
        : null,
    };
  } catch {
    return null;
  }
}

export function clearHomeSnapshot() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
